import type { EnrichCustomerResponse } from '@strata-monorepo/shared-types'
import type { Address } from '@strata-monorepo/shared-ui/types/Address'

interface OdysseyContextValue {
  getValue: (key: string) => unknown
}

/**
 * Complements enrichment API data with manual home info from the form.
 * Merges existing enrichment data (address, coordinates) with user-provided home details.
 */
export function complementEnrichmentWithFormData(
  enrichCustomerResponse: EnrichCustomerResponse | null,
  odysseyContext: OdysseyContextValue | null,
): EnrichCustomerResponse {
  // Get address data from form
  const address = odysseyContext?.getValue('address') as Address

  // Get manual home info data from form
  const homeSize = odysseyContext?.getValue('homeInfo.homeSize')
  const numBedrooms = odysseyContext?.getValue('homeInfo.numBedrooms')
  const numBathrooms = odysseyContext?.getValue('homeInfo.numBathrooms')
  const numFloors = odysseyContext?.getValue('homeInfo.numFloors')
  const yearBuilt = odysseyContext?.getValue('homeInfo.yearBuilt')

  // Map home size to square footage estimate
  const squareFootageMap: Record<string, number> = {
    apartment: 750,
    small: 1750,
    large: 3000,
  }

  // Build/complement enrichment response
  // Use existing enrichment data as base (if available), merge with manual home info
  return {
    metadata: enrichCustomerResponse?.metadata || {},
    results: {
      ...enrichCustomerResponse?.results,
      propertyData: {
        // Use enrichment API data if available, fallback to form address
        addressLine1:
          enrichCustomerResponse?.results?.propertyData?.addressLine1 || address?.street_line || '',
        city: enrichCustomerResponse?.results?.propertyData?.city || address?.city || '',
        state: enrichCustomerResponse?.results?.propertyData?.state || address?.state || '',
        zipCode: enrichCustomerResponse?.results?.propertyData?.zipCode || address?.zipcode || '',
        formattedAddress:
          enrichCustomerResponse?.results?.propertyData?.formattedAddress ||
          `${address?.street_line || ''}, ${address?.city || ''}, ${address?.state || ''} ${address?.zipcode || ''}`,
        id:
          enrichCustomerResponse?.results?.propertyData?.id ||
          `manual-${address?.zipcode || 'unknown'}`,
        latitude: enrichCustomerResponse?.results?.propertyData?.latitude || 0,
        longitude: enrichCustomerResponse?.results?.propertyData?.longitude || 0,
        // Always use manual home info from form (complementing the enrichment)
        bedrooms: numBedrooms ? parseInt(numBedrooms.toString().replace('+', ''), 10) : undefined,
        bathrooms: numBathrooms
          ? parseInt(numBathrooms.toString().replace('+', ''), 10)
          : ('' as unknown as number),
        squareFootage: squareFootageMap[homeSize as string] || 1750,
        features: {
          ...enrichCustomerResponse?.results?.propertyData?.features,
          floorCount: numFloors
            ? parseInt(numFloors.toString().replace('+', ''), 10)
            : ('' as unknown as number),
        },
        yearBuilt:
          yearBuilt && yearBuilt !== 'unknown'
            ? parseInt(yearBuilt.toString().split('-')[0] || '0', 10)
            : enrichCustomerResponse?.results?.propertyData?.yearBuiltImputed
              ? null
              : enrichCustomerResponse?.results?.propertyData?.yearBuilt || null,
      },
    },
  }
}
